import type { DailyExchangeRate } from '@/types';
import Colors from '@/constants/colors';

interface SimpleLineChartProps {
  data: DailyExchangeRate[];
  showGrid?: boolean;
}

export default function SimpleLineChart({
  data,
  showGrid = true,
}: SimpleLineChartProps) {
  if (!data || data.length === 0) {
    return (
      <div style={{ color: Colors.textSecondary, textAlign: 'center', padding: '20px' }}>
        No data available
      </div>
    );
  }

  // Calculate min and max for scaling
  const rates = data.map((d) => d.rate);
  const minRate = Math.min(...rates);
  const maxRate = Math.max(...rates);
  const range = maxRate - minRate;

  // Add 10% padding to top and bottom for better visualization
  const paddedMin = minRate - range * 0.1;
  const paddedMax = maxRate + range * 0.1;
  const paddedRange = paddedMax - paddedMin;

  // Fixed SVG dimensions in viewBox units (not percentages)
  const chartWidth = 400;
  const chartHeight = 200;
  const padding = { top: 10, right: 30, bottom: 25, left: 60 };

  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate SVG coordinates for data points
  const points = data.map((item, index) => {
    const xPercent = data.length === 1 ? 0.5 : index / (data.length - 1);
    const x = padding.left + xPercent * innerWidth;

    const yPercent = (item.rate - paddedMin) / paddedRange;
    const y = padding.top + (1 - yPercent) * innerHeight; // Inverted because SVG y increases downward

    return { x, y, ...item };
  });

  // Generate horizontal grid lines
  const gridLineCount = 4;
  const gridLines = showGrid
    ? Array.from({ length: gridLineCount + 1 }).map((_, i) => {
        const yPercent = i / gridLineCount;
        const y = padding.top + yPercent * innerHeight;
        const rateValue = paddedMax - yPercent * paddedRange;
        return { y, rate: rateValue };
      })
    : [];

  // Generate x-axis labels (dates)
  const xLabels = data.map((item, index) => {
    // Show 7 evenly distributed date labels
    const step = Math.max(1, Math.floor(data.length / 7));
    if (data.length <= 7 || index % step === 0 || index === data.length - 1) {
      const date = new Date(item.date);
      return {
        x: points[index].x,
        label: `${date.getDate()}/${date.getMonth() + 1}`,
      };
    }
    return null;
  }).filter((x) => x !== null);

  return (
    <div style={styles.container}>
      <svg
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        style={styles.svg}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Background */}
        <rect width={chartWidth} height={chartHeight} fill="transparent" />

        {/* Grid lines */}
        {gridLines.map((line, i) => (
          <g key={`grid-${i}`}>
            <line
              x1={padding.left}
              y1={line.y}
              x2={chartWidth - padding.right}
              y2={line.y}
              stroke={Colors.surface}
              strokeWidth="1"
              strokeDasharray="3,2"
              opacity="0.5"
            />
            <text
              x={padding.left - 8}
              y={line.y + 4}
              fontSize="12"
              textAnchor="end"
              fill={Colors.textSecondary}
              fontFamily="system-ui"
            >
              {line.rate.toFixed(2)}
            </text>
          </g>
        ))}

        {/* Axes */}
        <line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={chartHeight - padding.bottom}
          stroke={Colors.primary}
          strokeWidth="2"
        />
        <line
          x1={padding.left}
          y1={chartHeight - padding.bottom}
          x2={chartWidth - padding.right}
          y2={chartHeight - padding.bottom}
          stroke={Colors.primary}
          strokeWidth="2"
        />

        {/* Line chart */}
        <polyline
          points={points.map((p) => `${p.x},${p.y}`).join(' ')}
          fill="none"
          stroke={Colors.primary}
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* Data points */}
        {points.map((point, index) => (
          <circle
            key={`point-${index}`}
            cx={point.x}
            cy={point.y}
            r="3"
            fill={Colors.primary}
            opacity="0.8"
          />
        ))}

        {/* X-axis labels */}
        {xLabels.map((label, i) => (
          <g key={`date-${i}`}>
            <text
              x={label!.x}
              y={chartHeight - padding.bottom + 20}
              fontSize="12"
              textAnchor="middle"
              fill={Colors.textSecondary}
              fontFamily="system-ui"
            >
              {label!.label}
            </text>
          </g>
        ))}
      </svg>

      {/* Statistics */}
      <div style={styles.statistics}>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Min</span>
          <span style={styles.statValue}>{minRate.toFixed(2)}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Max</span>
          <span style={styles.statValue}>{maxRate.toFixed(2)}</span>
        </div>
        <div style={styles.statItem}>
          <span style={styles.statLabel}>Avg</span>
          <span style={styles.statValue}>
            {(rates.reduce((a, b) => a + b, 0) / rates.length).toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: Colors.surface,
    borderRadius: '12px',
    padding: '4px 8px 8px 8px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  } as React.CSSProperties,

  svg: {
    width: '100%',
    height: 'auto',
    minHeight: '250px',
    marginBottom: '2px',
  } as React.CSSProperties,

  statistics: {
    display: 'flex',
    justifyContent: 'space-around',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderTop: `1px solid ${Colors.primaryLight}`,
  } as React.CSSProperties,

  statItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,

  statLabel: {
    fontSize: '12px',
    color: Colors.textSecondary,
  } as React.CSSProperties,

  statValue: {
    fontSize: '14px',
    fontWeight: '600',
    color: Colors.primary,
  } as React.CSSProperties,
} as const;
